import { Form, FormValues, Transfer, TransferProps, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TransferItem } from 'antd/lib/transfer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LazyModal, { LazyModalComponentProps } from '../../../modules/ui/components/lazy-modal';
import { closeModal } from '../../../modules/ui/store/ui-slice';
import { useAppDispatch } from '../../../store/hooks';
import Modal from '../modal/modal';
import { columnsSelectorModalId } from './constants/constants';
import styles from './columns-selector.module.css';
import Sortable from 'sortablejs';
import { UserSettings } from '../../../modules/user/models/user-settings';
import { useSaveUserSettingMutation } from '../../../modules/user/api/user-api';
import { useSearchParams } from 'react-router-dom';

interface ColumnsSelectorProps<T> extends TransferProps<TransferItem> {
  columns: ColumnsType<T>;
  visibleColumns: ColumnsType<T>;
}

const immutableColumns: string[] = ['expand', 'actions'];

const ColumnsSelector = <T,>({
  columns,
  visibleColumns,
  isOpen,
  afterClose,
}: ColumnsSelectorProps<T> & LazyModalComponentProps): JSX.Element => {
  const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([]);
  const [sortedTargetKeys, setSortedTargetKeys] = useState<TransferProps['targetKeys']>([]);
  const [saveSetting, { isLoading: isLoadingSaveSetting }] = useSaveUserSettingMutation();
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const { t } = useTranslation('common');
  const [isSelectedAll, setIsSelectedAll] = useState(false);

  const [form] = Form.useForm<{ selectedColumns: string[] }>();

  useEffect(() => {
    const targetKeys = visibleColumns.map(column => column.key as string);

    setTargetKeys(targetKeys);
  }, [visibleColumns, columns]);

  const handleChange: TransferProps['onChange'] = newTargetKeys => {
    setTargetKeys(newTargetKeys);
  };

  const closeColumnsSelectorModal = (modalId: string) => {
    dispatch(closeModal(modalId));
  };

  const handleFormSubmit = ({ selectedColumns }: FormValues<typeof form>) => {
    const updatedVisibleColumns = columns
      .filter(column => (selectedColumns || []).includes(column.key as string))
      .sort((a, b) => selectedColumns.indexOf(a.key as string) - selectedColumns.indexOf(b.key as string));

    if (sortedTargetKeys?.length || visibleColumns.length) {
      const nonImmutableColumns = sortedTargetKeys?.length
        ? sortedTargetKeys
        : updatedVisibleColumns
            .map(visibleColumn => visibleColumn.key as string)
            .filter(key => !immutableColumns.includes(key));

      const columnsToSave = ['expand', ...nonImmutableColumns, 'actions'];

      saveSetting({
        name: UserSettings.SignRequestsTableColumns + (searchParams.get('template') || ''),
        value: columnsToSave.toString(),
      })
        .unwrap()
        .then(() => closeColumnsSelectorModal(columnsSelectorModalId));
    }
  };

  useEffect(() => {
    const initSortable = setTimeout(() => {
      const transferLists = document.getElementsByClassName('ant-transfer-list-content');
      if (!transferLists.length) return;

      Array.from(transferLists).forEach(listEl => {
        if (!(listEl instanceof HTMLElement)) return;

        Sortable.create(listEl, {
          animation: 150,
          onEnd: () => {
            const newTargetKeys: string[] = Array.from(
              listEl.querySelectorAll<HTMLElement>('.ant-transfer-list-content-item'),
            )
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              .map(el => el.getElementsByClassName('rendered-list-item')[0].getAttribute('data-key')!)
              .filter(Boolean);

            setSortedTargetKeys(newTargetKeys);
          },
        });
      });
    }, 100);

    return () => clearTimeout(initSortable);
  }, [targetKeys, isOpen]);

  return (
    <Modal
      title={t('signScanRequest.label.columnsFilter')}
      onCancel={() => closeColumnsSelectorModal(columnsSelectorModalId)}
      afterClose={afterClose}
      isOpen={isOpen}
      onOk={() => form.submit()}
      width={740}
      isLoading={isLoadingSaveSetting}
    >
      <div className={styles.descriptionText}>
        <Typography.Text>{t('columnSelector.description')}</Typography.Text>
      </div>
      <Form form={form} onFinish={handleFormSubmit}>
        <Form.Item
          name="selectedColumns"
          rules={[
            {
              validator: (_, value) => {
                if (value && value.length < 3) {
                  return Promise.reject(new Error(t('columnSelector.required')));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Transfer
            className={styles.transferComponent}
            oneWay
            titles={[t('columnSelector.available'), t('columnSelector.displayed')]}
            dataSource={columns
              .filter(column => !immutableColumns.includes(column.key as string))
              .map(
                column =>
                  ({
                    key: column.key,
                    title: column.title,
                  }) as Pick<TransferItem, 'key' | 'title'>,
              )}
            targetKeys={targetKeys}
            onChange={handleChange}
            render={item => (
              <div className="rendered-list-item" data-key={item.key}>
                {item.title}
              </div>
            )}
            listStyle={{
              minHeight: 250,
              // maxWidth is based on modal width
              maxWidth: 333,
              width: 'auto',
              minWidth: 200,
            }}
            locale={{
              selectAll: isSelectedAll ? t('columnSelector.unselectAllData') : t('columnSelector.selectAll'),
            }}
            onSelectChange={sourceSelectedKeys => {
              if (targetKeys && sourceSelectedKeys.length >= columns.length - targetKeys.length) {
                setIsSelectedAll(true);
              } else {
                setIsSelectedAll(false);
              }
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ColumnsSelectorModal = <T,>(props: ColumnsSelectorProps<T>) => {
  return (
    <LazyModal
      modalId={columnsSelectorModalId}
      Component={componentProps => <ColumnsSelector {...props} {...componentProps} />}
    />
  );
};

export default ColumnsSelectorModal;
